import axios from "axios";

/**
 * Shared axios instance. Paths in `API_ENDPOINTS` are host-absolute
 * (`/api/...`), resolved by:
 *   - dev: Vite proxy → mock server (:8000) or the Glow backend (set
 *     VITE_PROXY_TARGET to Glow's Django host so the session cookie flows)
 *   - prod: same-origin Django host
 */
export const apiClient = axios.create({
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

/** The session id the SPA mirrors in localStorage when sign-in sets the HttpOnly cookie. */
export function storedSessionId(): string | null {
  try {
    return localStorage.getItem("goodglows_sessionid");
  } catch {
    return null;
  }
}

/**
 * Session parity with Glow (see Glow `src/api/apiConfig.js`). Glow's backend
 * resolves the visitor from `goodglows_sessionid` — tracked in both a cookie
 * and localStorage (mobile-Safari resilience) — sent as a `?sessionid=` query
 * param, plus an `X-CSRFToken` header from the `csrftoken` cookie. Attaching
 * the same here means Secret Missions runs under the visitor's Glow session.
 *
 * Kept on purpose although the cookie alone serves same-origin requests: the
 * query param is what still identifies them when Safari drops the cookie. It
 * does put the session id in access logs — the price of that.
 */
apiClient.interceptors.request.use((config) => {
  const sessionid = getCookie("goodglows_sessionid") ?? storedSessionId();
  if (sessionid) {
    config.params = { ...config.params, sessionid };
  }
  const csrf = getCookie("csrftoken");
  if (csrf) {
    config.headers.set?.("X-CSRFToken", csrf);
  }
  return config;
});
